const installed = new WeakSet();

function stableParams(params) {
  if (!params) return '';
  return JSON.stringify(Object.keys(params).sort().map(key => [key, params[key]]));
}

export function installReadScheduler(client, maxReads = 4) {
  if (installed.has(client)) return;
  installed.add(client);
  let active = 0;
  const queue = [];
  const cache = new Map();
  const inflight = new Map();
  const baseAdapter = client.getAdapter(client.defaults.adapter);
  client.defaults.adapter = async config => {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get') {
      cache.clear();
      return baseAdapter(config);
    }
    const key = `${config.baseURL || ''}${config.url || ''}?${stableParams(config.params)}`;
    const ttl = Number.isFinite(config.vroomCacheTtlMs) ? Math.max(0, config.vroomCacheTtlMs) : 20_000;
    const cached = cache.get(key);
    if (ttl > 0 && cached && Date.now() - cached.at < ttl) return { ...cached.response, config };
    let request = inflight.get(key);
    if (!request) {
      request = baseAdapter(config);
      inflight.set(key, request);
    }
    try {
      const response = await request;
      if (ttl > 0 && response.status >= 200 && response.status < 300) cache.set(key, { at: Date.now(), response });
      return { ...response, config };
    } finally {
      if (inflight.get(key) === request) inflight.delete(key);
    }
  };
  const drain = () => {
    queue.sort((a, b) => a.priority - b.priority);
    while (active < maxReads && queue.length) {
      active += 1;
      const item = queue.shift();
      let released = false;
      item.resolve(() => {
        if (released) return;
        released = true;
        active -= 1;
        drain();
      });
    }
  };
  client.interceptors.request.use(async (config) => {
    if (String(config.method || 'get').toLowerCase() !== 'get') return config;
    const priority = ({ critical: 0, visible: 1, background: 2, prefetch: 3 })[config.vroomPriority] ?? 1;
    config.__vroomReadRelease = await new Promise(resolve => { queue.push({ priority, resolve }); drain(); });
    return config;
  });
  const finish = (value, failed = false) => {
    value?.config?.__vroomReadRelease?.();
    if (failed) return Promise.reject(value);
    return value;
  };
  client.interceptors.response.use(response => finish(response), error => finish(error, true));
}
